import { isTypedDetails, isCurriculumPointer } from './typed-details.js';
import { isCertificationProjection } from './certification-contracts.js';
import { isOptionalTimeZone } from './date-time-metadata.js';
/** A venue response violated the documented wire contract. */
export class VenueResponseContractError extends Error {
  constructor(operation, detail) {
    super(`Invalid ${operation} response: ${detail}`);
    this.name = 'VenueResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}

/** GET /api/venues returns VenueSummary[] directly, including [] when empty. */
export function readVenueListResponse(payload, operation = 'venue list') {
  if (!Array.isArray(payload)) {
    throw new VenueResponseContractError(operation, 'expected VenueSummary[].');
  }
  if (!payload.every(isVenueProjection)) {
    throw new VenueResponseContractError(operation, 'every venue must have a non-empty string _id.');
  }
  return payload;
}


/** Detail and creation/update endpoints return the complete venue directly. */
export function readVenueDetailResponse(payload, operation = 'venue detail') {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)
    || typeof payload._id !== 'string' || !payload._id
    || typeof payload.name !== 'string' || !payload.name
    || !['Residence', 'Online', 'CommunityVenue'].includes(payload.type)
    || typeof payload.community !== 'string' || !payload.community
    || !hasValidVenueDetails(payload)) {
    throw new VenueResponseContractError(operation, 'expected a venue with _id, name, type and community.');
  }
  return payload;
}

const venueAddressFields = ['formattedAddress', 'streetAddress', 'suburb', 'city', 'state', 'postalCode', 'countryCode', 'placeId'];
const venueReferences = ['family', 'household', 'ownerUser', 'contact'];
const record = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const text = value => value == null || typeof value === 'string';
const date = value => value == null || typeof value === 'string' && Number.isFinite(Date.parse(value));
const secretFree = value => !['password', 'passwordHash', 'refreshToken', 'emailVerificationCode', 'resetPasswordToken', 'resetPasswordExpires', 'verificationToken', 'emailVerificationToken', 'emailVerificationExpires'].some(field => Object.hasOwn(value, field))
  && value.verificationCode == null;
// Venue's older location subdocument can serialize its empty default array.
const point = value => value == null || record(value)
  && (value.type === undefined || value.type === 'Point')
  && (value.coordinates === undefined || Array.isArray(value.coordinates)
    && (value.coordinates.length === 0 || value.coordinates.length === 2)
    && value.coordinates.every(coordinate => typeof coordinate === 'number' && Number.isFinite(coordinate)));
function isVenueReference(value, depth = 0) {
  if (typeof value === 'string' && value.length > 0) return true;
  if (!record(value) || typeof value._id !== 'string' || !value._id || depth > 5 || !secretFree(value)) return false;
  return ['firstName', 'lastName', 'email', 'profilePicture', 'name', 'familyName', 'displayName', 'address', 'placeId'].every(field => text(value[field]))
    && ['isActive', 'acceptVenueRequest', 'allowRepeatResidenceRequests'].every(field => value[field] === undefined || typeof value[field] === 'boolean')
    && point(value.location)
    && ['community', 'household', 'family', 'primaryContact', 'venueApprover', 'userId'].every(field => value[field] == null || isVenueReference(value[field], depth + 1))
    && ['members', 'lineages'].every(field => value[field] === undefined || Array.isArray(value[field]) && value[field].every(item => isVenueReference(item, depth + 1)))
    && ['createdAt', 'updatedAt', 'startedAt', 'endedAt'].every(field => date(value[field]));
}
const optionalVenueReference = value => value == null || isVenueReference(value);

/** Known summary fields may be omitted by privacy or selected-field projections. */
export const isVenueProjection = venue => venue !== null && typeof venue === 'object' && !Array.isArray(venue)
  && typeof venue._id === 'string' && venue._id.length > 0
  && (venue.name === undefined || typeof venue.name === 'string')
  && (venue.type === undefined || ['Residence', 'Online', 'CommunityVenue'].includes(venue.type))
  && optionalVenueReference(venue.community) && hasValidVenueDetails(venue);

function hasValidVenueDetails(venue) {
  if (venue.requiresBookingEnd !== undefined && typeof venue.requiresBookingEnd !== 'boolean') return false;
  if (!isOptionalTimeZone(venue.timeZone)) return false;
  if (venue.availableForRequest !== undefined && typeof venue.availableForRequest !== 'boolean') return false;
  if (venue.onlineLink !== undefined && venue.onlineLink !== null && typeof venue.onlineLink !== 'string') return false;
  if (venue.notes !== undefined && venue.notes !== null && typeof venue.notes !== 'string') return false;
  if (!venueReferences.every(field => optionalVenueReference(venue[field]))) return false;
  if (venue.contactModel !== undefined && venue.contactModel !== null && !['User', 'Member'].includes(venue.contactModel)) return false;
  if (venue.address !== undefined && venue.address !== null && (typeof venue.address !== 'object' || Array.isArray(venue.address)
    || venueAddressFields.some(field => venue.address[field] !== undefined && typeof venue.address[field] !== 'string'))) return false;
  if (!point(venue.location) || !['createdAt', 'updatedAt'].every(field => date(venue[field]))) return false;
  if (venue.policy !== undefined && venue.policy !== null) {
    try { readVenuePolicyResponse(venue.policy, 'venue policy'); } catch { return false; }
  }
  return true;
}

const activityPolicies = new Set(['devotional', 'juniorYouth', 'childrensClass', 'studyCircle', 'fireside', 'initiative']);
const eventPolicies = new Set(['feast', 'holyDay', 'training', 'communityMeeting', 'infoNight', 'youthEvent']);

/** PUT /api/venues/:id/policy returns the policy, not a venue or wrapper. */
export function readVenuePolicyResponse(payload, operation = 'venue policy update') {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)
    || !Array.isArray(payload.activities) || !Array.isArray(payload.events)
    || payload.activities.some(value => !activityPolicies.has(value))
    || payload.events.some(value => !eventPolicies.has(value))) {
    throw new VenueResponseContractError(operation, 'expected { activities: ActivityPolicy[], events: EventPolicy[] }.');
  }
  return payload;
}


const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const isId = value => typeof value === 'string' && value.length > 0;
const isNullableString = value => value === null || typeof value === 'string';

function readVenueDecision(payload, operation) {
  if (!isRecord(payload) || !['ASSIGN', 'REQUEST'].includes(payload.action)
    || typeof payload.requiresApproval !== 'boolean' || !isNullableString(payload.reason)
    || (payload.disabled !== undefined && typeof payload.disabled !== 'boolean')
    || (payload.approverUserId !== undefined && payload.approverUserId !== null && !isId(payload.approverUserId))) {
    throw new VenueResponseContractError(operation, 'expected an explicit action, approval flag and nullable reason.');
  }
  return payload;
}

/** The resource preview always identifies the venue and a nullable User approver. */
export function readVenuePreviewResponse(payload, operation = 'venue preview') {
  readVenueDecision(payload, operation);
  if (!isId(payload.venueId) || (payload.approverUserId !== null && !isId(payload.approverUserId))) {
    throw new VenueResponseContractError(operation, 'expected venueId and nullable approverUserId.');
  }
  return payload;
}

function readEligibilityRows(payload, operation, resourcePreview) {
  if (!Array.isArray(payload)) throw new VenueResponseContractError(operation, 'expected an eligibility array.');
  for (const row of payload) {
    readVenueDecision(row, operation);
    readVenueDetailResponse(row.venue, operation);
    if (row.household !== undefined && row.household !== null && (!isRecord(row.household) || !isVenueReference(row.household))) {
      throw new VenueResponseContractError(operation, 'expected a household record or null.');
    }
    if (resourcePreview) {
      readVenuePreviewResponse(row, operation);
      if (row.venueId !== row.venue._id) throw new VenueResponseContractError(operation, 'venueId must identify the included venue.');
    }
  }
  return payload;
}

/** Existing session/event eligibility includes the same decision as /preview. */
export function readVenueEligibilityResponse(payload) {
  return readEligibilityRows(payload, 'venue eligibility', true);
}

/** Activity eligibility predates resources and has no required venueId field. */
export function readActivityVenueEligibilityResponse(payload) {
  return readEligibilityRows(payload, 'activity venue eligibility', false);
}

/** /find returns related venue, household and requestability records. */
export function readRelatedVenueResponse(payload) {
  const operation = 'related venues';
  if (!Array.isArray(payload)) throw new VenueResponseContractError(operation, 'expected a related-venue array.');
  for (const row of payload) {
    if (!isRecord(row) || !isRecord(row.requestability)
      || typeof row.requestability.requestable !== 'boolean' || !isNullableString(row.requestability.reason)
      || (row.household !== null && (!isRecord(row.household) || !isVenueReference(row.household)))) {
      throw new VenueResponseContractError(operation, 'expected venue, nullable household and explicit requestability.');
    }
    readVenueDetailResponse(row.venue, operation);
  }
  return payload;
}

/** Project validated activity rows into the flat options used by both pickers. */
export function toEligibleVenueOption(row) {
  return {
    ...row.venue,
    action: row.action,
    requiresApproval: row.requiresApproval,
    disabled: row.disabled === true,
    disabledReason: row.reason,
    approverUserId: row.approverUserId ?? null,
  };
}

/** Preserve backend requestability; neither picker re-infers permission. */
export function toRelatedVenueOption(row) {
  return {
    ...row.venue,
    ...(row.household ? {
      householdDetails: row.household,
      household: row.venue.household || row.household._id,
    } : {}),
    requestable: row.requestability.requestable,
    requestableReason: row.requestability.reason,
    disabled: !row.requestability.requestable,
    disabledReason: row.requestability.reason,
  };
}

const entity = value => record(value) && ['User', 'Member', 'Guest'].includes(value.type)
  && isVenueReference(value.refId) && (value.certifications == null || isCertificationProjection(value.certifications));
const group = value => value == null || record(value) && ['day', 'time', 'frequency'].every(field => text(value[field]));
const legacyActivity = value => isVenueReference(value) && (!record(value)
  || ['title', 'status', 'imageUrl'].every(field => text(value[field])) && group(value.groupDetails)
    && ['activityType', 'community', 'createdBy'].every(field => optionalVenueReference(value[field]))
    && (value.venues === undefined || Array.isArray(value.venues) && value.venues.every(venue => typeof venue === 'string' && venue.length > 0 || isVenueProjection(venue))));
const legacySession = value => record(value) && isId(value._id) && secretFree(value)
  && (value.status === undefined || ['Scheduled', 'Pending', 'Confirmed', 'Completed', 'Cancelled', 'Expired'].includes(value.status))
  && ['date', 'createdAt', 'updatedAt', 'confirmationEmailSent', 'reminderSentAt'].every(field => date(value[field]))
  && ['time', 'onlineLink'].every(field => text(value[field]))
  && (value.address == null || typeof value.address === 'string' || record(value.address) && venueAddressFields.every(field => text(value.address[field])))
  && optionalVenueReference(value.community) && optionalVenueReference(value.venueRequest)
  && (value.activity == null || legacyActivity(value.activity))
  && ['participants', 'facilitators', 'pendingParticipants', 'pendingFacilitators'].every(field => value[field] === undefined || Array.isArray(value[field]) && value[field].every(entity))
  && (value.venues === undefined || Array.isArray(value.venues) && value.venues.every(venue => typeof venue === 'string' && venue.length > 0 || isVenueProjection(venue)))
  && (value.venueRequests === undefined || Array.isArray(value.venueRequests) && value.venueRequests.every(isVenueReference))
  && isTypedDetails(value.details) && (value.curriculumPointer == null || isCurriculumPointer(value.curriculumPointer));

/** Legacy GET /api/venues/requests returns session/activity/household rows. */
export function readLegacyVenueRequestsResponse(payload) {
  if (!Array.isArray(payload)) {
    throw new VenueResponseContractError('legacy venue requests', 'expected a direct array.');
  }
  for (const row of payload) {
    if (!isRecord(row) || !legacySession(row.session)
      || (row.activity !== null && (!isRecord(row.activity) || !legacyActivity(row.activity)))
      || (row.household !== null && (!isRecord(row.household) || !isVenueReference(row.household)))) {
      throw new VenueResponseContractError('legacy venue requests', 'expected { session, activity, household }.');
    }
  }
  return payload;
}

export function readLegacyVenueRequestDecisionResponse(payload, operation = 'legacy venue request decision') {
  if (!isRecord(payload) || typeof payload.message !== 'string' || !payload.message
    || !legacySession(payload.session)
    || !isRecord(payload.request) || !isId(payload.request.sessionId)
    || payload.request.sessionId !== payload.session._id
    || (payload.request.householdId !== null && !isId(payload.request.householdId))) {
    throw new VenueResponseContractError(operation, 'expected { message, session, request }.');
  }
  return payload;
}

export function readLegacyVenueRequestAcceptanceResponse(payload) {
  readLegacyVenueRequestDecisionResponse(payload, 'legacy venue request acceptance');
  if (!isId(payload.request.householdId)) {
    throw new VenueResponseContractError('legacy venue request acceptance', 'expected a householdId.');
  }
  readVenueDetailResponse(payload.venue, 'accepted venue');
  return payload;
}

export function readVenueDeletionResponse(payload) {
  if (!isRecord(payload) || typeof payload.message !== 'string' || !payload.message) {
    throw new VenueResponseContractError('venue deletion', 'expected { message: string }.');
  }
  return payload;
}
